import { Box, Button } from '@ouster/webviz-react-components';

import DashboardCard from './DashboardCard';
import useChakraHook from '../../hooks/useChakraHook';
import { useStoreActions } from '../../reducers';

type UserUploaderStatusSyncProps = {
  compareUsersOrgsResult: {
    missing_organizations_in_uploader: Array<{
      org_name: string;
      tenant_id: string;
    }>;
    missing_users_in_uploader: Array<{
      email: string;
      identity_id: string;
    }>;
  } | null;
};

export default function UserUploaderStatusSync({
  compareUsersOrgsResult,
}: UserUploaderStatusSyncProps) {
  const {
    syncOrgWithUploader,
    syncUserWithUploader,
    setCompareUsersOrgsResult,
  } = useStoreActions((actions) => actions.adminDashboard);

  const { showToast } = useChakraHook();

  if (!compareUsersOrgsResult) return null;

  const {
    missing_organizations_in_uploader: missingOrgs,
    missing_users_in_uploader: missingUsers,
  } = compareUsersOrgsResult;

  const handleSyncOrg = async (tenantId: string) => {
    try {
      await syncOrgWithUploader({ tenant_id: tenantId });

      const newOrgs = missingOrgs.filter((org) => org.tenant_id !== tenantId);
      setCompareUsersOrgsResult({
        missing_organizations_in_uploader: newOrgs,
        missing_users_in_uploader: missingUsers,
      });

      showToast({
        title: 'Sync Complete',
        description: 'Organization sync initiated successfully.',
        status: 'success',
      });
    } catch (error) {
      showToast({
        title: 'Failed to sync organization',
        description: error instanceof Error ? error.message : String(error),
        status: 'error',
      });
    }
  };

  const handleSyncUser = async (identityId: string) => {
    try {
      await syncUserWithUploader({ identity_id: identityId });
      const newUsers = missingUsers.filter(
        (user) => user.identity_id !== identityId
      );
      setCompareUsersOrgsResult({
        missing_organizations_in_uploader: missingOrgs,
        missing_users_in_uploader: newUsers,
      });

      showToast({
        title: 'User Synced',
        description: 'User sync initiated successfully.',
        status: 'success',
      });
    } catch (error) {
      showToast({
        title: 'Failed to sync user',
        description: `${error}`,
        status: 'error',
      });
    }
  };

  return (
    <DashboardCard
      title="Sync Uploader and Studio Status"
      description="Sync the status of users and organizations between Uploader and Studio"
      data-testid="user-uploader-status-sync-card"
      style={{
        alignItems: 'flex-start',
        display: 'grid',
        gridTemplateRows: 'auto 1fr',
        overflow: 'hidden',
      }}
    >
      <Box
        display="grid"
        gap={8}
        gridTemplateColumns="1fr 1fr"
        overflow="hidden"
        height="100%"
      >
        <DashboardCard
          title="Missing Organizations in Uploader"
          data-testid="missing-organizations-card"
          style={{
            display: 'grid',
            gridTemplateRows: 'auto 1fr',
            overflow: 'hidden',
          }}
        >
          {missingOrgs.length > 0 ? (
            <Box overflowY="auto" height="100%">
              <Box display="grid" gap={4} pr={4}>
                {missingOrgs.map((org) => (
                  <Box
                    key={org.tenant_id}
                    display="flex"
                    gap={4}
                    alignItems="center"
                    justifyContent="space-between"
                    pb={4}
                    borderBottom="1px solid"
                    borderColor="gray.600"
                  >
                    <Box as="span" fontWeight="medium">
                      {org.org_name}
                    </Box>
                    <Button
                      variant="outline"
                      padding="4px 8px"
                      colorScheme="gray"
                      size="sm"
                      onClick={() => handleSyncOrg(org.tenant_id)}
                    >
                      Sync
                    </Button>
                  </Box>
                ))}
              </Box>
            </Box>
          ) : (
            <p>Organizations are all synced between Studio and Uploader.</p>
          )}
        </DashboardCard>

        <DashboardCard
          title="Missing Users in Uploader"
          data-testid="missing-users-card"
          style={{
            display: 'grid',
            gridTemplateRows: 'auto 1fr',
            overflow: 'hidden',
          }}
        >
          {missingUsers.length > 0 ? (
            <Box overflowY="auto" height="100%">
              <Box display="grid" gap={4} pr={4}>
                {missingUsers.map((user) => (
                  <Box
                    key={user.identity_id}
                    display="flex"
                    gap={4}
                    alignItems="center"
                    justifyContent="space-between"
                    pb={4}
                    borderBottom="1px solid"
                    borderColor="gray.600"
                  >
                    <Box as="span" fontWeight="medium" display="block">
                      {user.email}
                    </Box>
                    <Button
                      variant="outline"
                      padding="4px 8px"
                      colorScheme="gray"
                      size="sm"
                      onClick={() => handleSyncUser(user.identity_id)}
                    >
                      Sync
                    </Button>
                  </Box>
                ))}
              </Box>
            </Box>
          ) : (
            <p>No missing users found.</p>
          )}
        </DashboardCard>
      </Box>
    </DashboardCard>
  );
}
